import { STATUS_FILL, type ChartDatum } from './chart-colors';
import styles from './DonutChart.module.css';

const RADIUS = 52;
const STROKE = 17;
const SIZE = 140;
const CENTER = SIZE / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
/** 2px of surface between adjacent fills, per the mark spec. */
const GAP_PX = 2;

export interface DonutChartProps {
  readonly data: readonly ChartDatum[];
  readonly centerLabel: string;
  readonly title: string;
}

/**
 * Status donut. Identity is carried by the legend — label plus value on every
 * row — so the chart never relies on color alone, which is what makes the
 * brand palette usable at its light-mode contrast ratios.
 */
export function DonutChart({ data, centerLabel, title }: DonutChartProps) {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const visible = data.filter((d) => d.value > 0);

  let offset = 0;
  const segments = visible.map((datum) => {
    const length = total > 0 ? (datum.value / total) * CIRCUMFERENCE : 0;
    const gap = visible.length > 1 ? GAP_PX : 0;
    const drawn = Math.max(length - gap, 0.001);
    const element = (
      <circle
        key={datum.label}
        className={styles.segment}
        r={RADIUS}
        cx={CENTER}
        cy={CENTER}
        fill="none"
        stroke={STATUS_FILL[datum.tone]}
        strokeWidth={STROKE}
        strokeDasharray={`${drawn} ${CIRCUMFERENCE - drawn}`}
        strokeDashoffset={-offset}
        transform={`rotate(-90 ${CENTER} ${CENTER})`}
      >
        <title>{`${datum.label}: ${datum.value}`}</title>
      </circle>
    );
    offset += length;
    return element;
  });

  return (
    <div className={styles.wrap}>
      <svg
        className={styles.figure}
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={`${title}. Total ${total}.`}
      >
        {total === 0 ? (
          <circle
            r={RADIUS}
            cx={CENTER}
            cy={CENTER}
            fill="none"
            stroke="var(--chart-idle)"
            strokeWidth={STROKE}
          />
        ) : (
          segments
        )}
        <text x={CENTER} y={CENTER - 4} textAnchor="middle" className={styles.centerValue}>
          {total}
        </text>
        <text x={CENTER} y={CENTER + 14} textAnchor="middle" className={styles.centerLabel}>
          {centerLabel}
        </text>
      </svg>

      <ul className={styles.legend}>
        {data.map((datum) => (
          <li key={datum.label} className={styles.row}>
            <span
              className={styles.swatch}
              style={{ background: STATUS_FILL[datum.tone] }}
              aria-hidden="true"
            />
            <span className={styles.name}>{datum.label}</span>
            <span className={styles.value}>{datum.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
